import React, { useState, useEffect, DragEvent, useRef } from 'react';
import Layout from '../../../components/Layout';
import { apiFetch, apiDownload } from '../../../lib/api';
import BACKEND_URL from '../../../config.js';
import { useFiltroSocieta } from '../../../hooks/useFiltroSocieta';

interface UtenteMin {
    id: number;
    nome: string;
}

interface Bilancio {
    id: number;
    societaId: number;
    societa: { id: number; nome: string };
    anno: number;
    tipo: 'UFFICIALE' | 'VERIFICA';
    commento: string | null;
    nomeFile: string;
    uploadedAt: string;
    uploadedBy: UtenteMin;
}

export default function BilanciPage() {
    const [bilanci, setBilanci] = useState<Bilancio[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isUploading, setIsUploading] = useState(false);

    // Stati permessi
    const [userRole, setUserRole] = useState<string>('');

    const {
        societaId: filterSocietaId,
        setSocietaId: setFilterSocietaId,
        listaSocieta: societaList,
        isUnica,
        pronta,
    } = useFiltroSocieta();

    // Form Upload
    const [uploadSocietaId, setUploadSocietaId] = useState<string>('');
    const currentYear = new Date().getFullYear();
    const [uploadAnno, setUploadAnno] = useState<string>(currentYear.toString());
    const [uploadTipo, setUploadTipo] = useState<'UFFICIALE' | 'VERIFICA'>('UFFICIALE');
    const [uploadCommento, setUploadCommento] = useState<string>('');
    const [uploadFile, setUploadFile] = useState<File | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Filtri Lista
    const [filterAnno, setFilterAnno] = useState<string>('');

    useEffect(() => {
        apiFetch<{ utente: { ruolo: string } }>('/auth/me').then(d => setUserRole(d.utente.ruolo)).catch(() => {});
    }, []);

    // Auto-fill upload society: per isUnica usa l'unica società, altrimenti segue il filtro archivio
    useEffect(() => {
        if (isUnica && societaList.length === 1) {
            setUploadSocietaId(societaList[0].id.toString());
        } else if (filterSocietaId) {
            setUploadSocietaId(filterSocietaId);
        }
    }, [isUnica, societaList, filterSocietaId]);

    useEffect(() => {
        fetchBilanci();
    }, [pronta, filterSocietaId, filterAnno]); // eslint-disable-line

    const fetchBilanci = async () => {
        if (!pronta) return;
        try {
            setIsLoading(true);
            const params = new URLSearchParams();
            if (filterSocietaId) params.append('societaId', filterSocietaId);
            if (filterAnno) params.append('anno', filterAnno);

            const res = await apiFetch<Bilancio[]>(`/api/bilanci?${params.toString()}`);
            setBilanci(res);
        } catch (err) {
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
    };

    const handleDrop = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            const droppedFile = e.dataTransfer.files[0];
            if (droppedFile.type === 'application/pdf') {
                setUploadFile(droppedFile);
            } else {
                alert("Sono permessi solo file PDF");
            }
        }
    };

    const handleUpload = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!uploadFile) {
            alert("Seleziona prima un file PDF da caricare!");
            return;
        }
        if (!isUnica && !uploadSocietaId) {
            alert("Seleziona la Società");
            return;
        }

        try {
            setIsUploading(true);
            const formData = new FormData();
            formData.append('societaId', uploadSocietaId);
            formData.append('anno', uploadAnno);
            formData.append('tipo', uploadTipo);
            formData.append('commento', uploadCommento);
            formData.append('file', uploadFile);

            const res = await fetch(`${BACKEND_URL}/api/bilanci`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: formData,
            });

            if (!res.ok) {
                const error = await res.json();
                throw new Error(error.error || 'Errore di caricamento');
            }

            // Reset form success
            setUploadFile(null);
            setUploadCommento('');
            if (fileInputRef.current) fileInputRef.current.value = '';

            // Refresh list
            fetchBilanci();
            alert('Bilancio caricato con successo!');
        } catch (err: any) {
            console.error(err);
            alert(err.message || "Si è verificato un errore durante l'upload");
        } finally {
            setIsUploading(false);
        }
    };

    const handleDownload = async (id: number, nomeFile?: string) => {
        try {
            // @api GET /api/bilanci/:id/download -> file stream (attachment)
            await apiDownload(`/api/bilanci/${id}/download`, nomeFile || `bilancio_${id}.pdf`);
        } catch (err) {
            console.error(err);
            alert('Errore nel download del bilancio');
        }
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm("Sei sicuro di voler eliminare questo bilancio definitivamente? L'azione non è reversibile.")) return;

        try {
            await apiFetch(`/api/bilanci/${id}`, { method: 'DELETE' });
            fetchBilanci();
        } catch (err: any) {
            console.error(err);
            alert(err.message || "Errore durante l'eliminazione");
        }
    };

    // Genera elenco anni 2015-2035
    const selectYears = [];
    for (let y = 2035; y >= 2015; y--) selectYears.push(y);

    return (
        <Layout title="Bilanci">
            {/* ── CARD UPLOAD ── */}
            <div className="bop-bilanci__upload-card">
                <h2 className="bop-bilanci__upload-title">Carica un Nuovo Bilancio</h2>

                <form onSubmit={handleUpload} className="bop-bilanci__upload-form">

                    <div className="bop-bilanci__form-col">
                        {!isUnica && (
                            <div className="bop-form-group">
                                <label className="bop-label">Società *</label>
                                <select
                                    value={uploadSocietaId}
                                    onChange={e => setUploadSocietaId(e.target.value)}
                                    className="bop-select"
                                    required
                                >
                                    <option value="">Seleziona società...</option>
                                    {societaList.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
                                </select>
                            </div>
                        )}

                        <div className="bop-bilanci__form-row">
                            <div className="bop-bilanci__form-row-item">
                                <label className="bop-label">Anno *</label>
                                <select
                                    value={uploadAnno}
                                    onChange={e => setUploadAnno(e.target.value)}
                                    className="bop-select"
                                    required
                                >
                                    {selectYears.map(y => <option key={y} value={y}>{y}</option>)}
                                </select>
                            </div>

                            <div className="bop-bilanci__form-row-item">
                                <label className="bop-label">Tipo Documento *</label>
                                <select
                                    value={uploadTipo}
                                    onChange={e => setUploadTipo(e.target.value as any)}
                                    className="bop-select"
                                    required
                                >
                                    <option value="UFFICIALE">Bilancio Ufficiale</option>
                                    <option value="VERIFICA">Bilancio di Verifica</option>
                                </select>
                            </div>
                        </div>

                        <div className="bop-form-group">
                            <label className="bop-label">Commento</label>
                            <textarea
                                placeholder="Note sul documento..."
                                value={uploadCommento}
                                onChange={e => setUploadCommento(e.target.value)}
                                className="bop-bilanci__textarea"
                            />
                        </div>
                    </div>

                    <div className="bop-bilanci__form-col">
                        <div
                            onDragOver={handleDragOver}
                            onDrop={handleDrop}
                            onClick={() => fileInputRef.current?.click()}
                            className={`bop-bilanci__dropzone${uploadFile ? ' bop-bilanci__dropzone--has-file' : ''}`}
                        >
                            <input
                                type="file"
                                accept="application/pdf"
                                ref={fileInputRef}
                                className="bop-bilanci__file-input-hidden"
                                onChange={(e) => {
                                    if (e.target.files && e.target.files.length > 0) {
                                        setUploadFile(e.target.files[0]);
                                    }
                                }}
                            />
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={uploadFile ? "#16a34a" : "#64748b"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="bop-bilanci__dropzone-icon">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                <polyline points="14 2 14 8 20 8"></polyline>
                                <line x1="12" y1="18" x2="12" y2="12"></line>
                                <line x1="9" y1="15" x2="15" y2="15"></line>
                            </svg>
                            {uploadFile ? (
                                <div>
                                    <div className="bop-bilanci__dropzone-selected-label">File selezionato:</div>
                                    <div className="bop-bilanci__dropzone-selected-name">{uploadFile.name}</div>
                                    <div className="bop-bilanci__dropzone-selected-hint">Clicca o trascina per cambiare</div>
                                </div>
                            ) : (
                                <div className="bop-bilanci__dropzone-placeholder">
                                    <span className="bop-bilanci__dropzone-link">Clicca per esplorare</span> oppure trascina e rilascia<br />
                                    <span className="bop-bilanci__dropzone-hint">Solo file PDF</span>
                                </div>
                            )}
                        </div>

                        <button
                            type="submit"
                            disabled={isUploading || !uploadFile}
                            className="bop-bilanci__upload-btn"
                        >
                            {isUploading ? (
                                <>
                                    <span className="bop-bilanci__upload-spinner"></span>
                                    Caricamento in corso...
                                </>
                            ) : (
                                'Carica Bilancio'
                            )}
                        </button>
                    </div>
                </form>
            </div>

            {/* ── LISTA BILANCI ── */}
            <div className="bop-bilanci__archive-card">
                <div className="bop-bilanci__archive-header">
                    <h2 className="bop-bilanci__archive-title">Archivio Bilanci</h2>

                    <div className="bop-bilanci__archive-filters">
                        {!isUnica && (
                            <select
                                value={filterSocietaId}
                                onChange={e => setFilterSocietaId(e.target.value)}
                                className="bop-bilanci__filter-select"
                            >
                                <option value="">Tutte le società...</option>
                                {societaList.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
                            </select>
                        )}
                        <select
                            value={filterAnno}
                            onChange={e => setFilterAnno(e.target.value)}
                            className="bop-bilanci__filter-select"
                        >
                            <option value="">Tutti gli anni...</option>
                            {selectYears.map(y => <option key={y} value={y}>{y}</option>)}
                        </select>
                    </div>
                </div>

                {isLoading ? (
                    <div className="bop-bilanci__loading">Caricamento archivio...</div>
                ) : bilanci.length === 0 ? (
                    <div className="bop-bilanci__empty">
                        Nessun bilancio trovato per i filtri selezionati.
                    </div>
                ) : (
                    <div className="bop-bilanci__table-wrapper">
                        <table className="bop-bilanci__table">
                            <thead>
                                <tr>
                                    <th>Società</th>
                                    <th>Anno</th>
                                    <th>Tipo Documento</th>
                                    <th>Commento</th>
                                    <th>Caricato da</th>
                                    <th>Data Upload</th>
                                    <th className="bop-bilanci__th--actions">Azioni</th>
                                </tr>
                            </thead>
                            <tbody>
                                {bilanci.map((b) => (
                                    <tr key={b.id}>
                                        <td className="bop-bilanci__td-societa">{b.societa.nome}</td>
                                        <td className="bop-bilanci__td-anno">{b.anno}</td>
                                        <td className="bop-bilanci__td-tipo">
                                            <span className={`bop-badge ${b.tipo === 'UFFICIALE' ? 'bop-badge--success' : 'bop-badge--info'}`}>
                                                {b.tipo}
                                            </span>
                                        </td>
                                        <td className="bop-bilanci__td-commento">
                                            {b.commento ? (
                                                <span title={b.commento}>
                                                    {b.commento.length > 50 ? b.commento.substring(0, 50) + '...' : b.commento}
                                                </span>
                                            ) : (
                                                <span className="bop-bilanci__td-commento-empty">-</span>
                                            )}
                                        </td>
                                        <td className="bop-bilanci__td-meta">{b.uploadedBy.nome}</td>
                                        <td className="bop-bilanci__td-meta">{new Date(b.uploadedAt).toLocaleDateString()}</td>
                                        <td className="bop-bilanci__td-actions">
                                            <div className="bop-bilanci__actions-row">
                                                <button
                                                    onClick={() => handleDownload(b.id, b.nomeFile)}
                                                    className="bop-bilanci__btn-download"
                                                >
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
                                                    Scarica PDF
                                                </button>
                                                {userRole === 'SUPERADMIN' && (
                                                    <button
                                                        onClick={() => handleDelete(b.id)}
                                                        className="bop-bilanci__btn-delete"
                                                    >
                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>
                                                        Elimina
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

        </Layout>
    );
}
